import { ENTER } from '@angular/cdk/keycodes';
import { ComponentType } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter } from '@angular/material/core';
import {
    MatCalendar,
    MatCalendarHeader,
    MatCalendarView,
    MatMonthView,
    MatYearView,
} from '@angular/material/datepicker';
import { MatMultiYearView } from '@angular/material/datepicker/typings/multi-year-view';
import { AppStateFacade } from '@app-state';
import { isInternetExplorer } from '@uipath/angular/utilities';
import { unsubscribe } from '@utils/object.utils';

import groupBy from 'lodash-es/groupBy';
import isEqual from 'lodash-es/isEqual';
import * as moment from 'moment';
import {
    Subject,
    Subscription,
} from 'rxjs';
import {
    delay,
    filter,
    pairwise,
    startWith,
} from 'rxjs/operators';

const CalendarView = {
    Month: 'month' as 'month',
    Year: 'year' as 'year',
    MultiYear: 'multi-year' as 'multi-year',
};

export const YEARS_PER_PAGE = 24;

interface IRedrawOptions {
    view: MatCalendarView;
    date: moment.Moment;
}

interface IElementCollection extends Record<string, Element> { }
interface IDateRangeCollection extends Record<string, moment.Moment | null> { }

const emptyRedraw = {} as IRedrawOptions;

declare const IEKeyboardEvent: new (type: string, options: KeyboardEventInit) => KeyboardEvent;

/**
 * Overrides:
 * 'nextEnabled', 'previousEnabled'
 * and the Calendar's 'focusActiveCell'
 * This allows navigation through disabled calendar views
 */
export class OutOfBoundsNavigationHeader
    extends MatCalendarHeader<moment.Moment>
    implements OnInit {

    ngOnInit() {
        this.calendar.focusActiveCell = () => { };
    }
    // Override default header state flags
    public previousEnabled() { return true; }
    public nextEnabled() { return true; }
}

/**
 * Overrides:
 * 'clampDate'
 * This allows navigation through disabled calendar views
 */
export class OutOfBoundsDateAdapter extends MomentDateAdapter {
    private _languageChangeSub: Subscription;

    constructor(appState: AppStateFacade) {
        super(moment.locale());
        this._languageChangeSub = appState.language.change$
            .subscribe(
                () => this.setLocale(moment.locale()),
            );
    }

    public clampDate(date: moment.Moment) {
        return date;
    }

    public destroy() {
        unsubscribe(
            this._languageChangeSub,
        );
    }
}

interface DateCache {
    yesterday: moment.Moment;
    today: moment.Moment;
    tomorrow: moment.Moment;
}

const DATES = {
    yesterday: moment().add(-1, 'days'),
    tomorrow: moment().add(1, 'days'),
    today: moment(),
} as Readonly<DateCache>;

@Component({
    selector: 'ui-calendar',
    templateUrl: './ui-calendar.component.html',
    styleUrls: [
        './ui-calendar.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => UiCalendarComponent),
        multi: true,
    }, {
        provide: DateAdapter,
        useClass: OutOfBoundsDateAdapter,
        deps: [
            AppStateFacade,
        ],
    }],
})
export class UiCalendarComponent
    implements OnDestroy, ControlValueAccessor {

    @Input()
    public startAt: moment.Moment | null = null;

    @Input()
    public startView: MatCalendarView = CalendarView.Month;

    @Input()
    public selected: moment.Moment | null = null;

    @Input()
    public get minMonthDate() {
        return this._minDates[CalendarView.Month];
    }
    public set minMonthDate(value: moment.Moment | null) {
        this._minDates[CalendarView.Month] = value;
    }

    @Input()
    public get maxMonthDate() {
        return this._maxDates[CalendarView.Month];
    }
    public set maxMonthDate(value: moment.Moment | null) {
        this._maxDates[CalendarView.Month] = value;
    }

    @Input()
    public get minYearDate() {
        return this._minDates[CalendarView.Year];
    }
    public set minYearDate(value: moment.Moment | null) {
        this._minDates[CalendarView.Year] = value;
    }

    @Input()
    public get maxYearDate() {
        return this._maxDates[CalendarView.Year];
    }
    public set maxYearDate(value: moment.Moment | null) {
        this._maxDates[CalendarView.Year] = value;
    }

    @Input()
    public get minMultiYearDate() {
        return this._minDates[CalendarView.MultiYear];
    }
    public set minMultiYearDate(value: moment.Moment | null) {
        this._minDates[CalendarView.MultiYear] = value;
    }

    @Input()
    public get maxMultiYearDate() {
        return this._maxDates[CalendarView.MultiYear];
    }
    public set maxMultiYearDate(value: moment.Moment | null) {
        this._maxDates[CalendarView.MultiYear] = value;
    }

    public get viewMinDate() {
        if (
            this._activeView === CalendarView.Month &&
            this.disabled
        ) { return DATES.tomorrow; }

        return this._minDates[this._activeView];
    }

    public get viewMaxDate() {
        if (
            this._activeView === CalendarView.Month &&
            this.disabled
        ) { return DATES.yesterday; }

        return this._maxDates[this._activeView];
    }

    @HostBinding('attr.disabled')
    public get disabled() {
        const attributeValue = this._element.getAttribute('disabled') === 'true' ? true : null;

        if (this._disabled !== attributeValue) { this.disabled = attributeValue; }

        return this._disabled;
    }

    public set disabled(value) {
        if (this._disabled === value) { return; }

        this._disabled = !!value ? true : null;

        if (
            !this.calendar ||
            this.calendar.currentView !== CalendarView.Month
        ) { return; }

        this.calendar.monthView._init();
        this._cd.detectChanges();
    }

    public headerComponent: ComponentType<any> = OutOfBoundsNavigationHeader;

    @Output()
    public readonly selectedChange = new EventEmitter<moment.Moment>();

    @Output()
    public readonly yearSelected = new EventEmitter<moment.Moment>();

    @Output()
    public readonly monthSelected = new EventEmitter<moment.Moment>();

    @ViewChild(MatCalendar, { static: false })
    public set calendar(calendar: MatCalendar<moment.Moment>) {
        this._calendar = calendar;
        this._destroy();
        this._init();
    }
    public get calendar() {
        return this._calendar;
    }

    private _element: HTMLElement;
    private _dates: moment.Moment[] = [];
    private _eventDestructors: Function[] = [];
    private _keyDestructors: Function[] = [];
    private _redraw$ = new Subject<IRedrawOptions>();
    private _labels: IElementCollection = {};
    private _calendar!: MatCalendar<moment.Moment>;
    private _redrawSub!: Subscription;
    private _viewChangeSub!: Subscription;
    private _minDates = {} as IDateRangeCollection;
    private _maxDates = {} as IDateRangeCollection;
    private _disabled: boolean | null = false;
    private get _activeView() {
        return !!this.calendar ?
            this.calendar.currentView :
            this.startView;
    }

    constructor(
        private _renderer: Renderer2,
        private _dateAdapter: DateAdapter<moment.Moment>,
        private _cd: ChangeDetectorRef,
        _element: ElementRef,
    ) {
        this._element = _element.nativeElement;
    }

    ngOnDestroy() {
        (this._dateAdapter as any as OutOfBoundsDateAdapter).destroy();
        this._destroy();
    }

    public onChange: (_: any) => void = () => { };
    public onTouch: () => void = () => { };

    public writeValue(value: (moment.Moment | Date)[]) {
        if (
            !value ||
            isEqual(this._dates, value)
        ) { return; }

        const normalizedValue = value.map(d => {
            if (moment.isMoment(d)) { return d; }
            if (moment.isDate(d)) { return moment(d); }

            throw new Error(`Invalid date provided ${d ? JSON.stringify(d, null, 4) : null}`);
        });

        this._dates = [...normalizedValue]
            .sort((a, b) => a.unix() - b.unix());

        this._redraw$.next();
        this._cd.markForCheck();
    }

    public registerOnChange(fn: (_: any) => void): void {
        this.onChange = (date) => {
            this._redraw$.next();
            fn(date);
        };
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    public dateFilter = () => {
        return true;
    }

    public selectDate(date: moment.Moment) {
        if (
            this.viewMinDate &&
            this._dateAdapter.compareDate(date, this.viewMinDate) < 0 ||
            this.viewMaxDate &&
            this._dateAdapter.compareDate(date, this.viewMaxDate) > 0
        ) { return; }

        const idx = this._dates.findIndex(
            d => d.date() === date.date() &&
                d.month() === date.month() &&
                d.year() === date.year(),
        );

        if (idx > -1) {
            this._dates.splice(idx, 1);
        } else {
            this._dates.push(date);
        }

        this._dates = [...this._dates]
            .sort((a, b) => a.unix() - b.unix());

        this.calendar.activeDate = date;
        this.selectedChange.emit(date);
        this.onChange(this._dates);
        this.onTouch();
    }

    public selectMonth(date: moment.Moment) {
        this.monthSelected.emit(date);
        this._redraw$.next({
            view: CalendarView.Month,
            date: date,
        });
        this.onTouch();
    }

    public selectYear(date: moment.Moment) {
        this.yearSelected.emit(date);
        this._redraw$.next({
            view: CalendarView.Year,
            date: date,
        });
        this.onTouch();
    }

    public goToDate(date: moment.Moment) {
        this.calendar._goToDateInView(date, 'month');
    }

    private _init() {
        this._eventDestructors.push(
            ...this._redrawTrigger(
                '.mat-calendar-previous-button',
                '.mat-calendar-next-button',
                '.mat-calendar-period-button',
                '.mat-calendar-period-button',
            ),
        );

        this._redrawSub = this._redraw$
            .pipe(delay(0))
            .subscribe(
                (options: IRedrawOptions = emptyRedraw) => this._markCalendar(
                    (options.view || this.calendar.currentView),
                    (options.date || this.calendar.activeDate),
                ),
            );

        this._redraw$.next();
    }

    private _destroy() {
        this._invokeEventDestructors(this._eventDestructors);
        this._invokeEventDestructors(this._keyDestructors);
        unsubscribe(this._redrawSub);
    }

    private _markCalendar(view: MatCalendarView, filterDate: moment.Moment) {
        switch (view) {
            case CalendarView.Month:
                this._watchViewChange(this.calendar.monthView);
                return this._markMonthView(filterDate);
            case CalendarView.Year:
                this._watchViewChange(this.calendar.yearView);
                return this._markYearView(filterDate);
            case CalendarView.MultiYear:
                this._watchViewChange(this.calendar.multiYearView);
                return this._markMultiYearView();
        }
    }

    private _markMonthView(filterDate: moment.Moment) {
        const view = this._element.querySelector('mat-month-view');

        if (!view) { return; }

        const activeMonth = this._dateAdapter.getMonth(filterDate);
        const activeYear = this._dateAdapter.getYear(filterDate);

        const selectedDays = this._dates.filter(
            date => this._dateAdapter.getMonth(date) === activeMonth &&
                this._dateAdapter.getYear(date) === activeYear,
        ).map(
            date => this._dateAdapter.getDateNames()[date.get('date') - 1],
        );

        Array.from(this._element.querySelectorAll<HTMLElement>('.mat-calendar-body-cell'))
            .forEach(element => {
                const day = ((element.querySelector('.mat-calendar-body-cell-content') || {} as any)
                    .textContent || '')
                    .trim();

                if (!selectedDays.includes(day)) {
                    this._renderer.removeClass(element, 'date-selected');
                    this._renderer.setAttribute(element, 'aria-selected', 'false');
                } else {
                    this._renderer.addClass(element, 'date-selected');
                    this._renderer.setAttribute(element, 'aria-selected', 'true');
                }
            });

    }

    private _markYearView(filterDate: moment.Moment) {
        const view = this._element.querySelector('mat-year-view');

        if (!view) { return; }

        const activeYear = this._dateAdapter.getYear(filterDate);

        const monthCounts = groupBy(
            this._dates.filter(
                date => this._dateAdapter.getYear(date) === activeYear,
            ),
            (date) => this._dateAdapter.getMonth(date),
        );

        Array.from(this._element.querySelectorAll('.mat-calendar-body-cell-content'))
            .forEach((element, idx) => {
                this._appendCountLabel(element, monthCounts[idx], idx);
            });
    }

    private _markMultiYearView() {
        const view = this._element.querySelector('mat-multi-year-view');

        if (!view) { return; }

        const countAggregate = groupBy(this._dates, (date) => this._dateAdapter.getYearName(date));

        Array.from(this._element.querySelectorAll('.mat-calendar-body-cell-content'))
            .forEach((element) => {
                const key = element
                    .innerHTML
                    .trim().replace(
                        Array.from(element.children)
                            .map((child: Element) => child.outerHTML.trim())
                            .join(),
                        '',
                    );
                this._appendCountLabel(element, countAggregate[key], key);
            });
    }

    private _appendCountLabel(el: Element, list: moment.Moment[], key: string | number) {
        const hasCount = !!list;

        if (this._labels[key]) {
            this._renderer.removeChild(el, this._labels[key]);
            this._renderer.removeClass(el, 'populated');
        }

        this._labels[key] = this._renderer.createElement('span');
        this._renderer.addClass(this._labels[key], 'date-count-label');

        if (!hasCount) {
            this._renderer.addClass(this._labels[key], 'date-count-label-empty');
        }

        this._renderer.appendChild(this._labels[key], this._renderer.createText(`${
            hasCount ?
                list.length :
                0
            }`));

        this._renderer.appendChild(el, this._labels[key]);
        this._renderer.addClass(el, 'populated');
    }

    private _redrawTrigger(...selectors: string[]) {
        return selectors.map(selector =>
            this._renderer.listen(
                this._element.querySelector(selector),
                'click',
                () => {
                    this._redraw$.next();
                    this.onTouch();
                },
            ),
        );
    }

    private _watchViewChange(view: MatMonthView<moment.Moment> | MatYearView<moment.Moment> | MatMultiYearView<moment.Moment>) {
        unsubscribe(this._viewChangeSub);

        const rows = Array.from(this._element.querySelectorAll('.mat-calendar-body-cell'));

        this._invokeEventDestructors(this._keyDestructors);

        this._keyDestructors = rows.map(row => {
            return this._renderer.listen(row, 'keydown', (ev: KeyboardEvent) => {
                if (ev.keyCode === ENTER) {
                    // Prevent ENTER key from marking the currently focused day and then immediately submitting the form.
                    ev.stopPropagation();
                    this._element.dispatchEvent(this._createKeyboardEvent('keydown', ENTER, 'Enter'));
                }
            });
        });

        this._viewChangeSub = view.activeDateChange
            .pipe(
                startWith(this.calendar.activeDate),
                pairwise(),
                filter(([previous, current]) => !this._isSameView(previous, current)),
            )
            .subscribe(
                () =>
                    this._redraw$.next(),
            );
    }

    private _isSameView(previous: moment.Moment, current: moment.Moment): boolean {
        if (this.calendar.currentView === 'month') {
            return this._dateAdapter.getYear(previous) === this._dateAdapter.getYear(current) &&
                this._dateAdapter.getMonth(previous) === this._dateAdapter.getMonth(current);
        }
        if (this.calendar.currentView === 'year') {
            return this._dateAdapter.getYear(previous) === this._dateAdapter.getYear(current);
        }
        // Otherwise we are in 'multi-year' view.
        return Math.floor(this._dateAdapter.getYear(previous) / YEARS_PER_PAGE) ===
            Math.floor(this._dateAdapter.getYear(current) / YEARS_PER_PAGE);
    }

    private _invokeEventDestructors(destructors: Function[]) {
        destructors.forEach(
            destructor => destructor(),
        );
    }

    private _createKeyboardEvent(type: string, keyCode: number, key?: string) {
        const ctor = isInternetExplorer() ? IEKeyboardEvent : KeyboardEvent;
        const options: KeyboardEventInit = {
            code: `${keyCode}`,
            key: key,
            bubbles: true,
        };
        return new ctor(type, options);
    }
}
